import {
	firestore as db, functions
} from '@jaymathew/ui-shared/apis/FirebaseUtilities/FirebaseConfig'
import { encodeAndTokenizeForQueries } from '@jaymathew/utils/Search'
import {
	collection,
	DocumentData,
	getDocs,
	limit,
	orderBy,
	query,
	QueryDocumentSnapshot,
	startAfter,
	where
} from 'firebase/firestore'
import { httpsCallable, HttpsCallableResult } from 'firebase/functions'
import { Comunity } from '../resources/types'
import { getSelectedCompanyId } from 'shared/functions/selectedCompanyAccess'

export interface GetCommunitiesReturnType<T> {
	payload: T[],
	lastDocument?: QueryDocumentSnapshot<DocumentData>
}

export const getCommunities = async (sortBy: keyof Comunity, sort: 'asc' | 'desc' = 'asc', limitNumber?: number, searchTerm?: string, lastDocument?: QueryDocumentSnapshot<DocumentData>, limitToRegion?: string): Promise<{ payload: Comunity[], lastDocument?: QueryDocumentSnapshot<DocumentData> }> => {
	const companyID = getSelectedCompanyId() ?? ''
	const collectionRef = collection(db, `companies/${companyID}/communities`)
	let communitiesCollectionRef = query(collectionRef, orderBy(sortBy.toString(), sort))

	if (searchTerm) {
		const encodedSearchTerm = await encodeAndTokenizeForQueries(searchTerm)
		communitiesCollectionRef = query(communitiesCollectionRef, where('searchTerms', 'array-contains-any', encodedSearchTerm))
	}

	if (limitToRegion && limitToRegion.length > 0) {
		communitiesCollectionRef = query(communitiesCollectionRef, where('region', '==', limitToRegion))
	}

	if (lastDocument) {
		communitiesCollectionRef = query(communitiesCollectionRef, startAfter(lastDocument))
	}

	if (limitNumber !== undefined) {
		communitiesCollectionRef = query(communitiesCollectionRef, limit(limitNumber))
	}

	const communitiesCollection = await getDocs(communitiesCollectionRef)

	if (communitiesCollection.empty) {

		return {
			payload: [],
			lastDocument: undefined,
		}
	}

	const communities: Comunity[] = communitiesCollection.docs.map((doc) => doc.data() as Comunity)

	return {
		payload: communities,
		lastDocument: communitiesCollection.docs[communitiesCollection.docs.length - 1],
	}
}

export const addCommunity = async ({ name, region }: { name: string, region: string }) => {
	const companyID = getSelectedCompanyId() ?? ''
	const cloudfunction = httpsCallable(functions, 'firebasefuncCreateCommunity')

	// call the function with the parms
	const result: HttpsCallableResult = await cloudfunction({
		companyID,
		name,
		region,
	})

	// return what the cloud function returned
	return result
}

export const deleteCommunity = async ({ communityID }: { communityID: string }) => {
	const companyID = getSelectedCompanyId() ?? ''
	// create the Ref to the cloud callable function.
	const cloudfunction = httpsCallable(functions, 'firebasefuncDeleteCommunity')

	// call the function with the parms
	const result: HttpsCallableResult = await cloudfunction({
		companyID,
		communityID,
	})

	// return what the cloud function returned
	return result
}

export const updateCommunity = async ({
	communityID, name, region,
}: { communityID: string, name: string, region: string }) => {
	const companyID = getSelectedCompanyId() ?? ''
	// create the Ref to the cloud callable function.
	const cloudfunction = httpsCallable(functions, 'firebasefuncEditCommunity')

	// call the function with the parms
	const result: HttpsCallableResult = await cloudfunction({
		companyID,
		communityID,
		name,
		region,
	})

	// return what the cloud function returned
	return result
}
